import { useMerchantContext } from "lib/contexts/MerchantProvider";
import React, { useCallback, useMemo, useState } from "react";
import { NotePencil, FileText } from "@phosphor-icons/react";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import BillingAddressForm from "components/address/dialogs/BillingAddressForm";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents } from "lib/utils/constants";
import { useLocale } from "lib/hooks/useLocale";
import { useUserContext } from "lib/contexts/UserProvider";
import { getUserAddressById } from "lib/utils/address";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { truncateString, removeLastPincode } from "lib/utils/helpers";

interface BillingAddressProps {};

const BillingAddress: React.FC<BillingAddressProps> = React.memo(({}) => {
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { isC2P },
  } = useCheckoutContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { t } = useLocale();
  const {
    state: { user },
  } = useUserContext();

  const [openBillingAddrForm, setOpenBillingAddrForm] = useState<boolean>(false);

  const billingAddress = useMemo(() => {
    const defaultBillingAddressId = user?.default_billing_address?.id ?? "";
    return getUserAddressById(defaultBillingAddressId, user?.addresses);
  }, [user]);

  const handleBillingAddressBtnClick = () => {
    if (isC2P) return;
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_BILLING_ADDRESS_CHANGE_CLICKED,
      eventType: "click",
    });
    setOpenBillingAddrForm(true);
  };

  const getBillingTileSummary = useCallback(() => {
    const defaultShippingAddressId = user?.default_shipping_address?.id;
    const defaultBillingAddressId = user?.default_billing_address?.id;

    if (!defaultBillingAddressId) return <></>;
    if (!Boolean(billingAddress?.gstin) || !Boolean(billingAddress?.gst_business_name)) return <></>;

    const isBillingCopyOfShipping = defaultShippingAddressId === defaultBillingAddressId;
    if (isBillingCopyOfShipping) return <></>;

    return (
      <div className="flex w-full flex-col space-y-2 p-2 bg-gray-lightest rounded-lg whitespace-nowrap">
        <p className="w-full px-[3px] text-left text-xs font-normal text-coal-light leading-5 line-clamp-2">
          <span className="font-normal text-coal-light">
            {truncateString(removeLastPincode(user?.default_billing_address?.address ?? ""), 75)}
          </span>
          <span className="text-coal-dark font-medium ml-2">{user?.default_billing_address?.zip}</span>
        </p>
        <div className="flex flex-row space-x-3 px-[3px] text-gray-dark">
          <span className="text-xs font-normal">{`${billingAddress?.gst_business_name?.slice(0, 20)}${(billingAddress?.gst_business_name?.length ?? 0) > 20 ? "..." : ""}`}</span>
          <span className="text-xs font-normal truncate">{`GSTIN: ${billingAddress?.gstin}`}</span>
        </div>
      </div>
    );
  }, [user, billingAddress]);

  if (!Boolean(merchant?.addressConfig?.isBillingAddressEnabled)) return <></>;

  if (Boolean(isC2P) && (!Boolean(billingAddress?.gstin) || !Boolean(billingAddress?.gst_business_name)))
    return <></>;

  return (
    <>
      <button
        id="billing-address-section"
        className={`border-base flex w-full flex-col items-center gap-3 bg-white mt-3 p-3 ${isC2P ? "cursor-default" : "cursor-pointer"}`}
        onClick={handleBillingAddressBtnClick}>
        <div className="flex w-full flex-row items-center">
          <div className="flex w-full items-center space-x-2">
            <FileText className="h-5 w-5 text-coal-dark outline-none" />
            <h2 className="text-sm font-medium text-coal-dark">
              {t("billing_gst")}{" "}
              <span className="font-normal text-gray-dark">{`(${t("optional_text")})`}</span>
            </h2>
          </div>
          {!isC2P && <NotePencil className="h-5 w-5 cursor-pointer text-coal-dark" />}
        </div>

        {getBillingTileSummary()}
      </button>

      <GenericDialog
        isOpen={openBillingAddrForm}
        setIsOpen={setOpenBillingAddrForm}
        translateAxis="y"
        dialogOverlay={true}
        customClass="overflow-scroll md:!top-auto md:absolute h-[95%] md:h-[81vh] md:top-[4vh]"
        modalType="BILLING_ADDRESS_NEW">
        <BillingAddressForm setIsOpen={() => setOpenBillingAddrForm(false)} />
      </GenericDialog>
    </>
  );
});

export default BillingAddress;
